import { theme, type ThemeConfig } from 'antd';

/** Brand beige — used on dark surfaces where ~3.3:1 against the bg reads fine. */
const ACCENT_DARK = '#b0967d';
const ACCENT_DARK_HOVER = '#c4ab94';
/** Lighter brand beige for light mode — closer to the dark-mode original
 *  while still readable as button / link text on pure white. */
const ACCENT_LIGHT = '#947a62';
const ACCENT_LIGHT_HOVER = '#a98d72';

const sharedFamily = {
  borderRadius: 8,
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  fontSize: 15,
};

export const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    ...sharedFamily,
    colorPrimary: ACCENT_LIGHT,
    colorLink: ACCENT_LIGHT,
    colorLinkHover: ACCENT_LIGHT_HOVER,
    // Tokens kept in sync with the CSS variables in global.css :root so
    // AntD components and our raw-CSS rules render the same contrast.
    // Pure white body across all pages; cards rely on border + shadow.
    colorBgContainer: '#ffffff',
    colorBgLayout: '#ffffff',
    colorText: '#0d0d0d',
    colorTextSecondary: '#404040',
    colorTextTertiary: '#6b6b6b',
    colorTextDescription: '#404040',
    colorBorder: '#dcdcdc',
    colorBorderSecondary: '#ebebeb',
    colorBgElevated: '#ffffff',
    colorSplit: 'rgba(0, 0, 0, 0.12)',
    // Shadow tokens so any AntD component asking for boxShadow gets the
    // same lift as our `.ant-card` rule in global.css.
    boxShadow:
      '0 1px 2px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05)',
    boxShadowSecondary:
      '0 6px 24px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)',
    boxShadowTertiary: '0 1px 2px rgba(0,0,0,0.04)',
    // Neutral light-grey surface fill — chips / tags / subtle panels.
    colorFillTertiary: '#f0f0f0',
    colorFillSecondary: '#e8e8e8',
    colorFillQuaternary: '#f5f5f5',
  },
  components: {
    Layout: {
      headerBg: 'rgba(255, 255, 255, 0.92)',
      bodyBg: '#ffffff',
      footerBg: '#141414',
    },
    Button: {
      primaryColor: '#ffffff',
      borderRadius: 6,
    },
    Card: {
      borderRadiusLG: 12,
      /* Default Card lift so AntD Cards don't melt into the pure-white
         body. Same values as `.ant-card` in global.css for consistency. */
      boxShadowTertiary:
        '0 1px 2px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05)',
    },
    Input: {
      borderRadius: 6,
    },
    Menu: {
      itemBg: 'transparent',
    },
    Tag: {
      // Light-grey chip palette so category/tech tags read clearly on the
      // pure-white body without competing with the warm brand accent.
      defaultBg: '#f0f0f0',
      defaultColor: '#404040',
    },
  },
};

export const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...sharedFamily,
    colorPrimary: ACCENT_DARK,
    colorLink: ACCENT_DARK,
    colorLinkHover: ACCENT_DARK_HOVER,
    colorBgContainer: '#1a1a1a',
    colorBgLayout: '#0a0a0a',
    colorText: '#ededed',
    colorTextSecondary: '#b0b0b0',
    colorTextTertiary: '#8a8a8a',
    colorTextDescription: '#b0b0b0',
    colorBorder: '#353535',
    colorBorderSecondary: '#262626',
    colorBgElevated: '#1a1a1a',
    colorSplit: 'rgba(255, 255, 255, 0.14)',
  },
  components: {
    Layout: {
      headerBg: 'rgba(10, 10, 10, 0.85)',
      bodyBg: '#0a0a0a',
      footerBg: '#000000',
    },
    Button: {
      primaryColor: '#ffffff',
      borderRadius: 6,
    },
    Card: {
      borderRadiusLG: 12,
    },
    Input: {
      borderRadius: 6,
    },
    Menu: {
      itemBg: 'transparent',
    },
    Tag: {
      defaultBg: '#262626',
      defaultColor: '#d4d4d4',
    },
  },
};
